import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Draw, DrawDraft, InboundInvoice, Project } from '../types'
import { fmtDate, fmtMoney, todayISO } from '../lib/format'
import { confirmAction } from '../lib/confirm'
import Badge, { type BadgeColor } from '../components/Badge'

const STATUS_COLOR: Record<Draw['status'], BadgeColor> = {
  draft: 'gray',
  submitted: 'gold',
  funded: 'green',
}

export default function DrawsTab({ project }: { project: Project }) {
  const [draws, setDraws] = useState<Draw[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<Draw>>({})
  const [queue, setQueue] = useState<InboundInvoice[]>([])
  const [preview, setPreview] = useState<DrawDraft | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [submittingId, setSubmittingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [opMessage, setOpMessage] = useState<string | null>(null)

  useEffect(() => {
    api.draws.list(project.id).then(setDraws)
    api.draws
      .queue(project.id)
      .then(setQueue)
      .catch(() => setQueue([]))
  }, [project.id])

  async function refreshAll() {
    setDraws(await api.draws.list(project.id))
    try {
      setQueue(await api.draws.queue(project.id))
    } catch {
      setQueue([])
    }
  }

  async function showPreview() {
    setPreviewing(true)
    setError(null)
    try {
      const r = await api.draws.draftPreview(project.id)
      setPreview(r.draft)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setPreviewing(false)
    }
  }
  async function commit() {
    if (!preview) return
    if (!confirm(`Create Draw #${preview.draw_number} for ${fmtMoney(preview.total_request)}?`)) return
    setCommitting(true)
    setError(null)
    try {
      const r = await api.draws.commitDraft(project.id)
      setOpMessage(`Draw #${r.draft.draw_number} created as draft. Review then submit to DCA Title.`)
      setPreview(null)
      await refreshAll()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setCommitting(false)
    }
  }
  async function submitDraw(d: Draw) {
    const recipient = prompt(
      'Recipient email at title company:',
      project.title_contact_email ?? 't.glad@dcatitle.com'
    )
    if (!recipient) return
    if (!confirm(`Send Draw #${d.draw_number} (${fmtMoney(d.total_amount)}) to ${recipient}?`)) return
    setSubmittingId(d.id)
    setError(null)
    try {
      const r = await api.draws.submit(d.id, { recipient_email: recipient })
      setOpMessage(`Sent: ${r.attachments_sent} attachments. Draw status flipped to submitted.`)
      await refreshAll()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmittingId(null)
    }
  }
  async function markFunded(d: Draw) {
    if (!confirm(`Mark Draw #${d.draw_number} funded? This updates SCS line balances.`)) return
    setSubmittingId(d.id)
    try {
      const r = await api.draws.markFunded(d.id, { funded_date: todayISO() })
      setOpMessage(`Funded. Updated ${r.scs_lines_updated} SCS lines.`)
      await refreshAll()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmittingId(null)
    }
  }

  const totals = useMemo(() => {
    const submitted = draws.reduce(
      (s, d) => s + (d.status !== 'draft' ? d.total_amount : 0),
      0
    )
    const funded = draws.reduce((s, d) => s + (d.status === 'funded' ? d.total_amount : 0), 0)
    const gcReceived = draws.reduce(
      (s, d) => s + (d.status === 'funded' ? d.gc_fee_amount : 0),
      0
    )
    return { submitted, funded, gcReceived }
  }, [draws])

  async function add() {
    const d = await api.draws.create(project.id, {
      ...draft,
      total_amount: Number(draft.total_amount) || 0,
      gc_fee_amount: Number(draft.gc_fee_amount) || 0,
      status: 'draft',
    })
    setDraws((p) => [...p, d])
    setAdding(false)
    setDraft({})
  }

  async function setStatus(d: Draw, status: Draw['status']) {
    const label = status === 'submitted' ? 'submitted to DCA Title' : status === 'funded' ? 'funded' : status
    if (
      !confirmAction(
        `Mark Draw ${d.draw_number} as ${label}?\n\n` +
          `Total: ${fmtMoney(d.total_amount)}\n` +
          `GC fee portion: ${fmtMoney(d.gc_fee_amount)}\n\n` +
          (status === 'funded'
            ? 'Funded draws should match what DCA Title actually disbursed. Confirm the wire/check landed before marking.'
            : 'Audit log captures this state change.')
      )
    )
      return
    const updated = await api.draws.update(d.id, {
      status,
      ...(status === 'submitted' && !d.date_submitted ? { date_submitted: todayISO() } : {}),
      ...(status === 'funded' && !d.date_funded ? { date_funded: todayISO() } : {}),
    })
    setDraws((p) => p.map((x) => (x.id === d.id ? updated : x)))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Draws</h2>
          <p className="muted small">
            {draws.length} of {project.total_draws_allowed} allowed · {fmtMoney(project.construction_loan_amount)} loan
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Draw'}
        </button>
      </div>

      <div className="callout small mb-3">
        <strong>Flow:</strong> Heritage submits draw → Deerwood reviews → DCA Title disburses to subs
        directly + collects lien waivers. Heritage gets GC fee installment.
      </div>

      {error && <div className="card alert mb-2">{error}</div>}
      {opMessage && <div className="card mb-2"><strong>OK.</strong> {opMessage}</div>}

      {/* v2 Module 4: Draw queue + draft */}
      <div className="card mb-3" style={{ background: 'rgba(212,175,55,0.04)' }}>
        <div className="row-between">
          <div>
            <h3 style={{ margin: 0 }}>
              Invoice queue <span className="muted small">({queue.length} ready)</span>
            </h3>
            <p className="muted small">
              Invoices arriving via Inbox land here once you queue them for the next draw.
            </p>
          </div>
          {queue.length > 0 && (
            <button className="btn-primary" onClick={showPreview} disabled={previewing}>
              {previewing ? 'Building…' : 'Draft next draw'}
            </button>
          )}
        </div>
        {queue.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, marginTop: 8 }}>
            {queue.map((q) => (
              <li key={q.id} className="small">
                • {q.vendor_name ?? '(vendor)'} #{q.invoice_number ?? '?'} —{' '}
                {fmtMoney(q.total_amount ?? 0)}{' '}
                <span className="muted">
                  → {q.scs_item_label ?? 'no SCS match'}
                  {q.line_number ? ` (L${q.line_number})` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}

        {preview && (
          <div className="card mt-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <h4 style={{ marginTop: 0 }}>
              Draft Draw #{preview.draw_number} —{' '}
              <span className="muted">total {fmtMoney(preview.total_request)}</span>
            </h4>
            <p className="small">
              Sub invoices: {fmtMoney(preview.invoice_subtotal)} · Heritage GC fee installment:{' '}
              {fmtMoney(preview.gc_fee_installment)}
            </p>

            {preview.unmatched_invoices.length > 0 && (
              <div className="card alert">
                <strong>{preview.unmatched_invoices.length} invoice(s) without an SCS match.</strong>{' '}
                Resolve in the Inbox tab before submitting.
              </div>
            )}

            <table className="mt-2" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Line</th>
                  <th style={{ textAlign: 'left' }}>SCS item</th>
                  <th style={{ textAlign: 'right' }}>Requested</th>
                  <th style={{ textAlign: 'right' }}>New drawn</th>
                  <th style={{ textAlign: 'right' }}>Remaining</th>
                  <th style={{ textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((l, i) => (
                  <tr
                    key={i}
                    style={{
                      background: l.exceeds_budget ? 'rgba(204,68,68,0.10)' : undefined,
                    }}
                  >
                    <td>{l.line_number ?? '—'}</td>
                    <td>{l.scs_item_label}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(l.requested_amount)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(l.new_drawn_total)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(l.remaining_after)}</td>
                    <td className="small">
                      {l.warnings.length > 0 && <span style={{ color: '#cc4444' }}>⚠ {l.warnings.join(' / ')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="row mt-2 gap-2">
              <button
                className="btn-primary"
                onClick={commit}
                disabled={committing || preview.unmatched_invoices.length > 0}
              >
                {committing ? 'Creating…' : 'Create draft draw'}
              </button>
              <button className="btn-ghost" onClick={() => setPreview(null)}>
                Discard preview
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="stat-grid mb-3">
        <Stat label="Total Submitted" value={fmtMoney(totals.submitted)} />
        <Stat label="Total Funded" value={fmtMoney(totals.funded)} />
        <Stat
          label="Heritage GC Fee Received"
          value={fmtMoney(totals.gcReceived)}
          sub={`of ${fmtMoney(project.heritage_gc_fee)}`}
        />
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Total amount</span>
            <input
              type="number"
              value={draft.total_amount as number | undefined}
              onChange={(e) =>
                setDraft({ ...draft, total_amount: parseFloat(e.target.value) || 0 })
              }
              placeholder="75869"
            />
          </label>
          <label className="form-row">
            <span>GC fee portion (Heritage)</span>
            <input
              type="number"
              value={draft.gc_fee_amount as number | undefined}
              onChange={(e) =>
                setDraft({ ...draft, gc_fee_amount: parseFloat(e.target.value) || 0 })
              }
              placeholder="35000"
            />
          </label>
          <label className="form-row">
            <span>Notes</span>
            <input
              value={draft.notes ?? ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Pre-construction work + mobilization"
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Create Draw
          </button>
        </div>
      )}

      {draws.map((d) => (
        <div key={d.id} className="card mb-2">
          <div className="row-between">
            <div>
              <div className="row-title">
                Draw {String(d.draw_number).padStart(2, '0')} · {fmtMoney(d.total_amount)}
              </div>
              <div className="muted small">
                {d.date_submitted ? `submitted ${fmtDate(d.date_submitted)}` : 'not submitted'}
                {d.date_funded ? ` · funded ${fmtDate(d.date_funded)}` : ''}
              </div>
              <div className="muted small">GC fee portion: {fmtMoney(d.gc_fee_amount)}</div>
              {d.notes && <div className="muted small mt-2">{d.notes}</div>}
            </div>
            <div className="text-right">
              <Badge color={STATUS_COLOR[d.status]}>{d.status.toUpperCase()}</Badge>
            </div>
          </div>
          <div className="row gap-2 mt-2">
            {d.status === 'draft' && (
              <>
                <button
                  className="btn-primary small"
                  onClick={() => submitDraw(d)}
                  disabled={submittingId === d.id}
                >
                  {submittingId === d.id ? 'Sending…' : 'Submit to DCA Title (auto)'}
                </button>
                <button className="btn-ghost small" onClick={() => setStatus(d, 'submitted')}>
                  Mark submitted (manual)
                </button>
              </>
            )}
            {d.status === 'submitted' && (
              <button
                className="btn-primary small"
                onClick={() => markFunded(d)}
                disabled={submittingId === d.id}
              >
                {submittingId === d.id ? 'Updating…' : 'Mark funded → update SCS balances'}
              </button>
            )}
          </div>
        </div>
      ))}
    </section>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="muted small">{sub}</div>}
    </div>
  )
}
